import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["invariantBorderWidth", "innerWidth", "innerColor", "middleWidth", "middleColor", "outerWidth", "outerColor", "backgroundColor", "textColor", "boxShadow", "borderColor", "transition", "scope", "cursor", "type"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import memoize from "micro-memoize";
import { getFont } from "./fonts";
export { getFont };
/**
 * Return the corresponding icon based on the theme.
 * If the icon cannot be found for this theme, the
 * default icon for "ceo" will be returned.
 *
 * @param {String} name the name of the icon
 * @param {String} theme the name of the theme required for this icon.
 * @returns {SvgIcon}
 */

export async function getIcon(name, theme) {
  let icon;

  try {
    icon = await (await import(`../../Icons/${theme}/${name}`)).default;
  } catch (error) {
    /* istanbul ignore next */
    icon = await (await import(`../../Icons/ceo/${name}`)).default;
  }

  return icon;
}
/**
 * Return the theme configuration for the given component.
 * If the theme is not found, the default "ceo" theme is
 * returned instead.
 *
 * @async
 *
 * @param {String} name the name of the component file without extension
 * @param {String} theme the name of the theme required for this component
 * @returns {Object}
 */

const getComponentTheme = async (name, theme) => {
  let componentTheme;

  try {
    componentTheme = await (await import(`../${theme}/${name}`)).default;
  } catch (error) {
    /* istanbul ignore next */
    componentTheme = await (await import(`../ceo/${name}`)).default;
  }

  return componentTheme;
};
/**
 * Return the theme configuration for all components.
 *
 * @async
 *
 * @param {String} theme the name of the theme required
 * @returns {Object}
 */


export async function getAllComponentsTheme(theme) {
  return await import(`../${theme}/index.js`);
}
/**
 * Return an object representing properties of the current
 * theme for typography.
 *
 * @async
 *
 * @param {String} name the name of the theme
 * @returns {Object}
 *
 */

export async function getTypographyTheme(themeName) {
  return {
    typography: await getComponentTheme("typography", themeName)
  };
}
/**
 * Convert pixels to rem
 *
 * @param {Number | String} px A number in pixel, or a string to convert to rems.
 * @returns a string representing rems (including the "rem" suffix)
 */

export const pxToRem = memoize(px => {
  return `${parseInt(px, 10) / 16}rem`;
});
/**
 * getContrastColor method returns the font color
 * that is `A11Y - AA` standard compliant.
 *
 * It verifies if the passed color token are generated by
 * `Pioneer RIA` and then based on the shade code it returns
 * either black or white.
 *
 * @param {String} backgroundColor A token from a predefined list of tokens
 * that will be used as the background color. It does not accept a hexadecimal color as a value.
 * @param {String} defaultFontColor The hexadecimal color that should be used as the defaultFontColor.
 * for cases where it cannot process the passed token alias background color.
 * @returns font color that is `A11Y - AA` standard compliant,
 * based on the background color.
 */

export const getContrastColor = memoize(function (backgroundColor) {
  let defaultFontColor = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : "#181616";

  /**
   * verify if the token starts with `var(--`
   */
  if (backgroundColor.startsWith("var(--")) {
    const darkFontColor = "#181616";
    const lightFontColor = "#ffffff";
    let colorShadeCode = parseInt(backgroundColor.replace(/[a-z()-]/gi, ""), 10);
    colorShadeCode = isNaN(colorShadeCode) ? "" : colorShadeCode;
    /**
     * Every color has ten different shades and they
     * are suffixed by a number:
     * 1. colorShadeCode > 50 indicates shade is darker so font color
     * should be lighter.
     * 2. colorShadeCode <= 50 indicates shade is lighter so font color
     * has to be darker.
     */

    if (colorShadeCode > 50) {
      return lightFontColor;
    } else if (colorShadeCode <= 50) {
      return darkFontColor;
    }
  }

  return defaultFontColor;
});
/**
 * Example
 * border: 1px
 * hover/focus: outer 1px white, main 3px green (total focus size), inner 1px white
 *
 *    border: 1px solid some-color
 *    &:hover: {
 *      box-shadow: inset 0 0 0 1px #fff, 0 0 0 2px green, 0 0 0 3px #fff;
 *    }
 *
 *    borderSize => x = 1px
 *    boxShadowInnerSize => y = 1px
 *    boxShadowMainBorderSize => x + z = 3px (z = total focus size - borderSize)
 *    boxShadowOuterSize =>  boxShadowMainBorderSize + desired outer size
 *
 *  WARNING: if the desired middleWith is smaller than the
 *           original invariantBorderWidth, the middleWith will be forced to be
 *           exactly the same size as the invariantBorderWidth...
 */

export function createBorderForState(name, _ref) {
  let {
    invariantBorderWidth = 0,
    innerWidth = 0,
    innerColor = "transparent",
    middleWidth,
    middleColor,
    outerWidth = 0,
    outerColor = "transparent",
    backgroundColor,
    textColor = "currentcolor",
    boxShadow,
    borderColor = "transparent",
    transition = "initial",
    scope = "",
    cursor = "pointer",
    type = "focus"
  } = _ref,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  /* istanbul ignore next */
  if (process.env.NODE_ENV !== "production") {
    if (boxShadow && middleWidth) {
      const msg = `[${name}:${type}] boxShadow and middleWidth props conflict with each other, please choose one or the other.`; // eslint-disable-next-line no-console

      console.error(msg);
    }
  }

  const actualMiddleWidth = middleWidth - invariantBorderWidth;
  const actualOuterWidth = outerColor === "transparent" ? 0 : actualMiddleWidth + outerWidth;
  return {
    [`&${scope}:${type}`]: _objectSpread({
      boxShadow: boxShadow ? boxShadow : `inset 0 0 0 ${innerWidth}px ${innerColor}, 0 0 0 ${actualMiddleWidth}px ${middleColor}, 0 0 0 ${actualOuterWidth}px ${outerColor}`,
      borderColor: boxShadow ? borderColor : middleColor,
      backgroundColor: backgroundColor ? backgroundColor : "inherit",
      color: textColor,
      cursor,
      transition
    }, otherProps)
  };
}